
// Backend:
import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiUrl } from '../config/api';
import './SliderInfo.css';
import { useEffect } from 'react';
import { useState } from 'react';
interface SlideData {
  id: number;
  image: string;           // filename
  image_url: string;       // full URL from API
  title: string;
  buttonText: string;
  buttonLink: string;
  fullDescription?: string;
  date?: string;
  author?: string;
  category?: string;
  impactAreas?: string[];
}
interface LocationState {
  slideData: SlideData;
}

// Remove the hardcoded allSlides array completely!
// We'll use either navigation state or fetch from API


const SliderInfo: React.FC = () => {
  const { slideId } = useParams<{ slideId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [slideData, setSlideData] = useState<SlideData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
 
  useEffect(() => {
    fetchSlideData();
  }, [slideId]);

  const fetchSlideData = async () => {
    try {
      setLoading(true);
      
      // First, check if data was passed via navigation state
      const slideDataFromState = (location.state as LocationState)?.slideData;
      
      if (slideDataFromState) {
        // Use data from navigation state
        setSlideData(slideDataFromState);
        console.log('Using slide data from navigation state:', slideDataFromState);
      } else {
        // If no state data, fetch from API using slideId
        if (!slideId) {
          throw new Error('No slide ID provided');
        }
        
        console.log(`Fetching slide ${slideId} from API...`);
        const response = await fetch(apiUrl(`/api/slides/${slideId}`));
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        
        const result = await response.json();
        
        if (result.success) {
          setSlideData(result.data);
          console.log('Slide data from API:', result.data);
        } else {
          throw new Error('API returned unsuccessful response');
        }
      }
    } catch (err) {
      setError(`Failed to load slide: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Error fetching slide:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="slider-info-container">
        <div className="slider-info-content">
          <div className="slider-info-loading">
            <div className="loading-spinner"></div>
            <p>Loading slide information...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !slideData) {
    return (
      <div className="slider-info-container">
        <div className="slider-info-content">
          <div className="slider-info-not-found">
            <h2>Slide Information Not Found</h2>
            <p>{error || `The requested slide with ID ${slideId} could not be found.`}</p>
            <p className="error-help">
              Make sure the slide exists in the database and API is running.
            </p>
            <button onClick={() => navigate('/')} className="slider-info-back-button">
              <i className="fas fa-arrow-left"></i>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Use image_url from API, fallback to image
  const imageUrl = slideData.image_url || slideData.image;

  return (
    <div className="slider-info-container">
      <div className="slider-info-content">
        {/* Back Button */}
        <button onClick={() => navigate(-1)} className="slider-info-back-button">
          <i className="fas fa-arrow-left"></i>
          Back to Previous
        </button>

        {/* Debug info (remove in production) */}
        <div className="debug-info" style={{ display: 'none' }}>
          <p>Slide ID: {slideData.id}</p>
          <p>Image URL: {imageUrl}</p>
          <p>Data source: {location.state?.slideData ? 'Navigation State' : 'API'}</p>
        </div>

        {/* Slide Header */}
        <div className="slider-info-header">
          <h1 className="slider-info-title">{slideData.title}</h1>
          <div className="slider-info-meta">
            {slideData.date && (
              <span className="slider-info-date">
                <i className="fas fa-calendar"></i>
                {slideData.date}
              </span>
            )}
            {slideData.author && (
              <span className="slider-info-author">
                <i className="fas fa-user"></i>
                {slideData.author}
              </span>
            )}
            {slideData.category && (
              <span className="slider-info-category">
                <i className="fas fa-tag"></i>
                {slideData.category}
              </span>
            )}
          </div>
        </div>

        {/* Slide Image */}
        <div className="slider-info-image">
          <img 
            src={slideData.image_url} 
            alt={slideData.title}
            className="slider-info-main-image"
            onError={(e) => {
              console.error('Image failed to load:', slideData.image_url);
              // Fallback to placeholder
              (e.target as HTMLImageElement).src = `https://placehold.co/1200x600/2563eb/white?text=${encodeURIComponent(slideData.title)}`;
            }}
          />
        </div>

        
        {/* Slide Description */}
        <div className="slider-info-description">
          <h2>Project Overview</h2>
          <p className="slider-info-full">
            {slideData.fullDescription}
          </p>
          
          {slideData.impactAreas && slideData.impactAreas.length > 0 && (
            <div className="slider-info-impact-areas">
              <h4>Key Focus Areas:</h4>
              <div className="impact-areas-grid">
                 <div className="impact-areas-grid">
                  {slideData.impactAreas.map((area, index) => (
                    <div key={index} className="impact-area-card">
                      <i className="fas fa-check-circle"></i>
                      <span>{area}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="slider-info-actions">
          <button className="slider-info-share-btn">
            <i className="fas fa-share"></i>
            Share Project
          </button>
          <button className="slider-info-support-btn">
            <i className="fas fa-hands-helping"></i>
            Support This Initiative
          </button>
          <button 
            className="slider-info-contact-btn"
            onClick={() => navigate('/contact')}
          >
            <i className="fas fa-envelope"></i>
            Contact Us
          </button>
        </div>
      </div>
    </div>
  );
};

export default SliderInfo;
































// frontend
// import React from 'react';
// import { useParams, useNavigate, useLocation } from 'react-router-dom';
// import './SliderInfo.css';

// interface SlideData {
//   id: number;
//   image: string;
//   title: string;
//   description: string;
//   buttonText: string;
//   buttonLink: string;
//   fullDescription?: string;
//   date?: string;
//   author?: string;
//   category?: string;
//   impactAreas?: string[];
// }

// interface LocationState {
//   slideData: SlideData;
// }

// // Slide data that matches the IDs from Slider.tsx
// const allSlides: SlideData[] = [
//   {
//     id: 1,
//     image: "https://www.reha.org.af/wp-content/uploads/2025/04/Nature-Day.jpg",
//     title: "Sustainable Solutions for Climate and Crisis Response",
//     description: "REHA supports Afghan communities through climate action, disaster risk reduction, and humanitarian aid—blending innovation with compassion.",
//     buttonText: "Read More",
//     buttonLink: "sustainable-solutions",
//     fullDescription: "NORA's Sustainable Solutions program focuses on addressing the dual challenges of climate change and humanitarian crises in Afghanistan. Our comprehensive approach combines traditional knowledge with modern technology to create resilient communities. We work directly with local populations to develop climate-smart agriculture, disaster preparedness plans, and sustainable water management systems that withstand environmental shocks and stresses. Through community-led initiatives and partnerships with local authorities, we're building a future where Afghan communities can thrive despite environmental challenges.",
//     date: "January 15, 2024",
//     author: "NORA Climate Team",
//     category: "Climate Action",
//     impactAreas: ["Climate Resilience", "Disaster Response", "Community Empowerment", "Water Management", "Sustainable Agriculture"]
//   },
//   {
//     id: 2,
//     image: "https://www.reha.org.af/wp-content/uploads/2025/04/Tree-Planiton-Compaign.jpg", 
//     title: "Building Resilient Communities",
//     description: "Empowering local communities with sustainable development programs and emergency response initiatives.",
//     buttonText: "Learn More",
//     buttonLink: "resilient-communities",
//     fullDescription: "Our Building Resilient Communities initiative focuses on creating self-sustaining communities that can withstand economic, environmental, and social challenges. Through capacity building, infrastructure development, and local leadership training, we empower communities to drive their own development. The program includes vocational training, small business development, and community-based disaster risk management that puts local people in charge of their future. We've established community centers, trained local leaders, and supported the creation of community-led enterprises that provide sustainable livelihoods.",
//     date: "February 20, 2024", 
//     author: "NORA Community Development Team",
//     category: "Community Development",
//     impactAreas: ["Economic Empowerment", "Infrastructure Development", "Local Governance", "Vocational Training", "Small Business Support"]
//   },
//   {
//     id: 3,
//     image: "https://www.reha.org.af/wp-content/uploads/2025/04/National-Policy-Dialogue.jpg",
//     title: "Climate Action & Humanitarian Aid",
//     description: "Combining environmental protection with humanitarian assistance for lasting impact.",
//     buttonText: "Get Involved", 
//     buttonLink: "climate-action",
//     fullDescription: "The Climate Action & Humanitarian Aid program represents NORA's integrated approach to addressing both immediate needs and long-term sustainability. We combine emergency humanitarian assistance with climate adaptation measures, ensuring that communities not only survive crises but emerge stronger. Our work includes reforestation projects, clean energy initiatives, and climate education alongside essential humanitarian support like food security and healthcare access. This holistic approach has helped communities develop sustainable solutions while meeting their immediate basic needs.",
//     date: "March 10, 2024",
//     author: "NORA Integrated Programs Team", 
//     category: "Integrated Programs",
//     impactAreas: ["Environmental Conservation", "Humanitarian Aid", "Education", "Clean Energy", "Reforestation"]
//   }
// ];

// const SliderInfo: React.FC = () => {
//   const { slideId } = useParams<{ slideId: string }>();
//   const navigate = useNavigate();
//   const location = useLocation();
  
//   const defaultDescription = "NORA Non-Governmental Organization (NGO) was founded in 2025 by a dedicated team of professors from Kabul Polytechnic University and Kabul Medical University, along with experienced professionals who deeply understand the unique challenges and opportunities in Afghan society. Recognizing Afghanistan's rich cultural heritage and resilient spirit, our founders united with a common goal: to contribute to sustainable national development and improve the quality of life for all citizens, especially women and children.";

//   // Get slide data from navigation state first, then fall back to ID-based lookup
//   const slideDataFromState = (location.state as LocationState)?.slideData;
//   const slideIdNumber = parseInt(slideId || '0');
  
//   // Find slide data by ID if not available from state
//   const slideData = slideDataFromState || allSlides.find(slide => slide.id === slideIdNumber);

//   if (!slideData) {
//     return (
//       <div className="slider-info-not-found">
//         <h2>Slide Information Not Found</h2>
//         <p>The requested slide with ID {slideId} could not be found.</p>
//         <button onClick={() => navigate('/')} className="slider-info-back-button">
//           <i className="fas fa-arrow-left"></i>
//           Back to Home
//         </button>
//       </div>
//     );
//   }

//   return (
//     <div className="slider-info-container">
//       <div className="slider-info-content">
//         {/* Back Button */}
//         <button onClick={() => navigate(-1)} className="slider-info-back-button">
//           <i className="fas fa-arrow-left"></i>
//           Back to Home
//         </button>

//         {/* Slide Header */}
//         <div className="slider-info-header">
//           <h1 className="slider-info-title">{slideData.title}</h1>
//           <div className="slider-info-meta">
//             {slideData.date && (
//               <span className="slider-info-date">
//                 <i className="fas fa-calendar"></i>
//                 {slideData.date}
//               </span>
//             )}
//             {slideData.author && (
//               <span className="slider-info-author">
//                 <i className="fas fa-user"></i>
//                 {slideData.author}
//               </span>
//             )}
//             {slideData.category && (
//               <span className="slider-info-category">
//                 <i className="fas fa-tag"></i>
//                 {slideData.category}
//               </span>
//             )}
//           </div>
//         </div>

//         {/* Slide Image */}
//         <div className="slider-info-image">
//           <img 
//             src={slideData.image} 
//             alt={slideData.title}
//             className="slider-info-main-image"
//           />
//         </div>

//         {/* Slide Description */}
//         <div className="slider-info-description">
//           <h2>Project Overview</h2>
//           <p className="slider-info-brief">{slideData.description}</p>
//           <p className="slider-info-full">
//             {slideData.fullDescription || defaultDescription}
//           </p>
          
//           <div className="slider-info-additional">
//             <h3></h3>
//             <p>
              
//             </p>
            
//             {slideData.impactAreas && (
//               <div className="slider-info-impact-areas">
//                 <h4>Key Focus Areas:</h4>
//                 <div className="impact-areas-grid">
//                   {slideData.impactAreas.map((area, index) => (
//                     <div key={index} className="impact-area-card">
//                       <i className="fas fa-check-circle"></i>
//                       <span>{area}</span>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}
//           </div>
//         </div>

//         {/* Action Buttons */}
//         <div className="slider-info-actions">
//           <button className="slider-info-share-btn">
//             <i className="fas fa-share"></i>
//             Share Project
//           </button>
//           <button className="slider-info-support-btn">
//             <i className="fas fa-hands-helping"></i>
//             Support This Initiative
//           </button>
//           <button 
//             className="slider-info-contact-btn"
//             onClick={() => navigate('/contact')}
//           >
//             <i className="fas fa-envelope"></i>
//             Contact Us
//           </button>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SliderInfo;